import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';

const STATUSES = ['open', 'claimed', 'resolved', 'closed'];

const STATUS_COLOR = {
  open: 'bg-blue-100 text-blue-700 border border-blue-300',
  claimed: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
  resolved: 'bg-green-100 text-green-700 border border-green-300',
  closed: 'bg-grey-100 text-grey-700 border border-grey-300',
};

function StatCard({ title, value, subtitle, icon }) {
  return (
    <div className="bg-white border border-pampa-200 rounded-lg p-6 space-y-2 hover:shadow-lg transition">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-grey-600 tracking-wide">{title}</p>
        <div className="text-grey-400">{icon}</div>
      </div>
      <p className="text-3xl font-semibold text-grey-900">{value}</p>
      {subtitle && <p className="text-sm text-grey-500 font-medium">{subtitle}</p>}
    </div>
  );
}

export default function Inbox() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState('');
  const [productId, setProductId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/products').then(({ data }) => setProducts(data.products));
    api.get('/stats').then(({ data }) => setStats(data));
  }, []);

  function load() {
    setLoading(true);
    const params = {};
    if (status) params.status = status;
    if (productId) params.product_id = productId;
    api
      .get('/tickets', { params })
      .then(({ data }) => setTickets(data.tickets))
      .finally(() => setLoading(false));
  }

  useEffect(load, [status, productId]);

  const filtered = tickets.filter((t) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      t.id.toLowerCase().includes(q) ||
      t.subject.toLowerCase().includes(q) ||
      t.customers?.email?.toLowerCase().includes(q) ||
      t.customers?.full_name?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold text-grey-900 tracking-tight">Support Dashboard</h1>
        <p className="text-grey-600 font-medium tracking-wide">Manage and respond to customer tickets, across every product</p>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Tickets"
            value={stats.total || 0}
            subtitle="All time"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            }
          />
          <StatCard
            title="Open"
            value={stats.open || 0}
            subtitle="Awaiting response"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            title="In Progress"
            value={stats.claimed || 0}
            subtitle="Being handled"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            title="Resolved"
            value={stats.resolved || 0}
            subtitle="Successfully closed"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>
      )}

      <div className="bg-white border border-pampa-200 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-2 tracking-wide">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2 bg-white border border-pampa-200 rounded-lg text-grey-900 focus:outline-none focus:ring-2 focus:ring-grey-900 focus:border-transparent transition font-medium"
            >
              <option value="">All Tickets</option>
              {STATUSES.map((s) => (
                <option key={s} value={s} className="capitalize">
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-grey-700 mb-2 tracking-wide">Product</label>
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="w-full px-4 py-2 bg-white border border-pampa-200 rounded-lg text-grey-900 focus:outline-none focus:ring-2 focus:ring-grey-900 focus:border-transparent transition font-medium"
            >
              <option value="">All Products</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-grey-700 mb-2 tracking-wide">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tickets..."
              className="w-full px-4 py-2 bg-white border border-pampa-200 rounded-lg text-grey-900 placeholder-grey-400 focus:outline-none focus:ring-2 focus:ring-grey-900 focus:border-transparent transition font-medium"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={load}
              className="w-full px-4 py-2 bg-grey-900 text-white rounded-lg hover:bg-grey-800 transition font-medium tracking-wide flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-pampa-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-pampa-200 bg-pampa-50">
          <h2 className="text-lg font-semibold text-grey-900 tracking-wide">
            Tickets
            <span className="ml-2 text-sm font-medium text-grey-500">
              ({filtered.length} {filtered.length === 1 ? 'ticket' : 'tickets'})
            </span>
          </h2>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-grey-900"></div>
            <p className="text-grey-500 font-medium mt-2">Loading tickets...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <svg className="w-16 h-16 text-grey-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-grey-400 font-medium text-lg">No tickets found</p>
            <p className="text-grey-400 text-sm font-medium mt-1 tracking-wide">Try adjusting your filters or wait for new tickets to arrive</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-pampa-50 border-b border-pampa-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">Ticket</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">Assigned To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-pampa-200">
                {filtered.map((t) => (
                  <tr key={t.id} className="hover:bg-pampa-50 transition cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)}>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <p className="text-sm font-medium text-grey-900">{t.subject}</p>
                        <p className="text-xs text-grey-500 font-medium">#{t.id.slice(0, 8)}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-grey-900 font-medium">{t.products?.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${STATUS_COLOR[t.status]}`}>{t.status}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <p className="text-sm text-grey-900 font-medium">{t.customers?.full_name || 'N/A'}</p>
                        <p className="text-xs text-grey-500 font-medium">{t.customers?.email || 'N/A'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {t.staff ? (
                        <p className="text-sm text-grey-900 font-medium">{t.staff.full_name}</p>
                      ) : (
                        <span className="text-sm text-grey-500 font-medium italic">Unassigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <p className="text-sm text-grey-900 font-medium">{new Date(t.created_at).toLocaleDateString()}</p>
                        <p className="text-xs text-grey-500 font-medium">{new Date(t.created_at).toLocaleTimeString()}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tickets/${t.id}`);
                        }}
                        className="text-grey-900 hover:text-grey-700 font-medium text-sm"
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
